// Dashboard / Übersicht — configurable widget grid. Layout is stored
// per-user on the server. If the user has no own layout yet, the backend
// returns the assigned template's layout (lazy seed); the first edit
// persists their personal copy.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Plus, Settings2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  DashboardGrid,
  EMPTY_LAYOUT,
  type PersistedLayout,
} from '../components/dashboard/dashboard-grid'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { api } from '../lib/api'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardOverviewPage,
})

// Zusätzlicher Alias /dashboard — gleiche Komponente, intuitiverer URL.
export const DashboardAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardOverviewPage,
})

function isPersistedLayout(v: unknown): v is PersistedLayout {
  return (
    !!v &&
    typeof v === 'object' &&
    (v as PersistedLayout).version === 1 &&
    Array.isArray((v as PersistedLayout).widgets)
  )
}

function DashboardOverviewPage() {
  const qc = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const { data: layoutData, isLoading: layoutLoading } = useQuery({
    queryKey: ['dashboard', 'layout'],
    queryFn: () => api.getDashboardLayout(),
    enabled: !!meData?.user,
  })

  const saveMutation = useMutation({
    mutationFn: (layout: PersistedLayout) => api.saveDashboardLayout(layout),
    onError: (e: Error) => {
      // Surface but don't roll back local state — the user's edits stay
      // visible; next edit re-tries the save.
      console.error('Layout-Save fehlgeschlagen:', e.message)
    },
  })

  // Local mirror so drag/resize is instant even before the network round-trip
  // settles. Initialised from server data once it arrives.
  const [localLayout, setLocalLayout] = useState<PersistedLayout | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mobile-Shell (<640): Titel in der Top-Leiste; der In-Content-Kopf ist
  // dort ausgeblendet (phone:hidden), die Aktionen leben in Bottom-Slots.
  useMobilePageTitle('Dashboard')

  useEffect(() => {
    if (localLayout || !layoutData) return
    setLocalLayout(
      isPersistedLayout(layoutData.layout) ? (layoutData.layout as PersistedLayout) : EMPTY_LAYOUT,
    )
  }, [layoutData, localLayout])

  function handleChange(next: PersistedLayout) {
    setLocalLayout(next)
    // Debounced save — react-grid-layout fires onLayoutChange repeatedly
    // during drag, no need to round-trip on every microstep.
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      saveMutation.mutate(next, {
        onSuccess: () => {
          // Mark layout as user-owned in the cache so future GETs aren't
          // overridden by template fallback.
          qc.setQueryData(['dashboard', 'layout'], { layout: next, source: 'user' as const })
        },
      })
    }, 400)
  }

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const fromTemplate = layoutData?.source === 'template'
  const banner =
    fromTemplate && layoutData?.templateName ? (
      <div className="rounded-lg border border-info/30 bg-info/5 px-4 py-2 text-xs text-text-secondary">
        Aktuell wird die Vorlage <strong>{layoutData.templateName}</strong> angezeigt. Sobald du
        etwas änderst, wird daraus deine persönliche Kopie.
      </div>
    ) : null

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Layout-Edit-Toggle + ＋ Widget in der
          Bottom-Bar — Handler identisch zu den Kopf-Buttons darunter. */}
      <MobileBottomSlots>
        <BarSlot
          label={editMode ? 'Fertig' : 'Bearbeiten'}
          icon={<Settings2 className="h-5 w-5" />}
          active={editMode}
          ariaPressed={editMode}
          ariaLabel={editMode ? 'Layout-Bearbeitung beenden' : 'Layout bearbeiten'}
          onClick={() => setEditMode((v) => !v)}
        />
        <BarAccentPlus ariaLabel="Widget hinzufügen" onClick={() => setPickerOpen(true)} />
      </MobileBottomSlots>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Dashboard"
        actions={
          <>
            <Button
              variant={editMode ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setEditMode((v) => !v)}
              aria-label={editMode ? 'Layout-Bearbeitung beenden' : 'Layout bearbeiten'}
              title={editMode ? 'Layout-Bearbeitung beenden' : 'Layout bearbeiten'}
            >
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline"> {editMode ? 'Fertig' : 'Layout'}</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setPickerOpen(true)}
              aria-label="Widget hinzufügen"
              title="Widget hinzufügen"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline"> Widget</span>
            </Button>
          </>
        }
      />

      {layoutLoading || !localLayout ? (
        <p className="text-text-secondary">Lade Dashboard …</p>
      ) : (
        <DashboardGrid
          value={localLayout}
          onChange={handleChange}
          banner={banner}
          allowedWidgets={
            (meData?.user?.roleObj?.widgetVisibility as Record<string, boolean>) ?? null
          }
          editMode={editMode}
          onEditModeChange={setEditMode}
          pickerOpen={pickerOpen}
          onPickerOpenChange={setPickerOpen}
        />
      )}
    </div>
  )
}
